import React, { InputHTMLAttributes } from 'react';

import { UserIcon, WalletIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';

import TransparentCard from '@/components/shared/card/TransparentCard';
import TotalBalance from '@/components/TotalBalance';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useCashierShow } from '@/features/cashier/useCashier';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { BanknotesIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid';
import { useIntl } from 'react-intl';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

interface ButtonProps extends InputHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

const Button = ({ children, onClick, icon, className }: ButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        `p-2 sm:p-4 text-white font-bold text-base sm:text-xl leading-10 flex items-center justify-center gap-2
        bg-blue-dark rounded-lg`,
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
};

const ButtonsCenter = () => {
  const intl = useIntl();
  const showCashier = useCashierShow();
  const navigate = useAbsoluteNavigate();
  // Deposits are disabled in restricted mode — drop the Deposit action from the hero.
  const isRestricted = useRestrictedMode();

  const handleOnBalanceClick = () => {
    triggerGTag(GTagEvents.hero_btn_balance_click);
    showCashier();
  };

  const handleOnDepositClick = () => {
    triggerGTag(GTagEvents.hero_btn_deposit_click);
    showCashier();
  };

  const handleOnWithdrawClick = () => {
    triggerGTag(GTagEvents.hero_btn_withdraw_click);
    showCashier({ transactionType: 'withdrawal' });
  };

  const handleOnUserDetailsClick = () => {
    triggerGTag(GTagEvents.hero_btn_user_profile_click);
    navigate('/settings');
  };

  const handleOnTransactionsClick = () => {
    triggerGTag(GTagEvents.hero_btn_user_transactions_click);
    navigate('/settings/history');
  };

  return (
    <TransparentCard className="!rounded-[26px] !p-3.5 sm:!p-5">
      <div className="grid grid-cols-2 grid-rows-3 gap-2 max-w-sm">
        <Button
          onClick={handleOnBalanceClick}
          icon={<WalletIcon className="h-5 w-5" />}
          className="col-span-2"
        >
          <div className="flex items-center gap-2">
            <span>{intl.formatMessage({ id: 'misc.balance' })}:</span>
            <TotalBalance />
          </div>
        </Button>
        {!isRestricted && (
          <Button onClick={handleOnDepositClick} icon={<CreditCardIcon className="h-5 w-5" />}>
            {intl.formatMessage({ id: 'misc.deposit' })}
          </Button>
        )}
        <Button
          onClick={handleOnWithdrawClick}
          icon={<BanknotesIcon className="h-5 w-5" />}
          className={isRestricted ? 'col-span-2' : undefined}
        >
          {intl.formatMessage({ id: 'misc.withdraw' })}
        </Button>
        <Button onClick={handleOnUserDetailsClick} icon={<UserIcon className="h-5 w-5" />}>
          {intl.formatMessage({ id: 'misc.profile' })}
        </Button>
        <Button onClick={handleOnTransactionsClick} icon={<ArrowsUpDownIcon className="h-5 w-5" />}>
          {intl.formatMessage({ id: 'misc.transactions' })}
        </Button>
      </div>
    </TransparentCard>
  );
};

export default ButtonsCenter;
